import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, from, Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import {
  Announcements,
  Chats,
  CustomerLists,
  Customers,
  CustomerTeam,
  Memos,
  Notifications,
  Organisations,
  ResponseSuccessFamilyLeaderboardAction,
  ResponseSuccessMembersStatisticsAction,
} from '../actions';
import { Feed } from '../feed/feed.actions';
import { Requests } from '../requests/requests.actions';
import { Members } from '../members/members.actions';
import {
  ResponseSuccessPendingInvitations,
  ResponseSuccessSearchPendingInvitationsAdmin,
  RESPONSE_SUCCESS_INVITATIONS_PENDING,
  RESPONSE_SUCCESS_SEARCH_ALL_PENDING_INVITATIONS_ADMIN,
} from '../invite/invite.actions';
import {
  AnnouncementDto,
  CustomerListDto,
  InvitationDto,
  PermissionRequestDto,
} from '@careapp-group/ng-sdk';
import { CheckInDto, MemoResponseDto, NotificationDto } from '@careapp-group/ng-sdk';
import { Action } from '@ngrx/store';

@Injectable()
/**
 * Effects that fetch related data when new entities enter the store
 */
export class RelatedDataEffects {
  fetchOwnersForAnnouncements = createEffect(() =>
    this.actions$.pipe(
      ofType(Announcements.RESPONSE_SUCCESS_ALL, Announcements.RESPONSE_SUCCESS_BY_ID),
      switchMap((action: any) => {
        if (!action.payload) {
          return EMPTY;
        }
        // Work for both "all" and "by id" actions
        const announcements: AnnouncementDto[] = Array.isArray(action.payload)
          ? action.payload
          : [action.payload];
        const owners = new Set<string>(announcements.map((item) => (<any>item).memberId));
        return Array.from(owners)
          .filter((owner) => owner)
          .map((id) => Members.RequestByIdAction({ id }));
      })
    )
  );

  fetchOwnersForAnnouncementHistory = createEffect(() =>
    this.actions$.pipe(
      ofType(Announcements.ResponseSuccessHistory),
      switchMap((action) => {
        const owners = new Set<string>(action.history.map((historyItem) => historyItem.memberId));
        return Array.from(owners)
          .filter((owner) => !!owner)
          .map((id) => Members.RequestByIdAction({ id }));
      })
    )
  );

  fetchOwnersForFeedItems = createEffect(() =>
    this.actions$.pipe(
      ofType(Feed.RESPONSE_SUCCESS_ALL_PAGINATED),
      switchMap((action: any) => {
        if (!action.payload || !action.payload.items) {
          return EMPTY;
        }
        const owners = new Set<string>(action.payload.items.map((item) => (<any>item).memberId));
        return Array.from(owners)
          .filter((owner) => owner)
          .map((id) => Members.RequestByIdAction({ id }));
      })
    )
  );

  fetchOwnersForNotificationItems = createEffect(() =>
    this.actions$.pipe(
      ofType(Notifications.RESPONSE_SUCCESS_ALL_PAGINATED),
      switchMap((action: any) => {
        if (!action.payload || !action.payload.items) {
          return EMPTY;
        }
        const owners = new Set<string>(
          action.payload.items.map((item) => (<any>item)?.model?.owner)
        );
        const creators = new Set<string>(
          action.payload.items.map((item) => (<any>item)?.model?.memberId)
        );

        // If any notifications are for invitations, retrieve the invited users.
        const invitedUsers = new Set<string>(
          action.payload.items.reduce((prev, curr) => {
            return curr.modelName === 'Invitation' && curr.memberId != null
              ? [...prev, curr.memberId]
              : prev;
          }, [])
        );

        return [...Array.from(owners), ...Array.from(creators), ...Array.from(invitedUsers)]
          .filter((owner) => owner)
          .map((id) => Members.RequestByIdAction({ id }));
      })
    )
  );

  fetchOwnersForChats = createEffect(() =>
    this.actions$.pipe(
      ofType(Chats.RESPONSE_SUCCESS_ALL),
      switchMap((action: any) => {
        if (!action.payload || action.payload.length === 0) {
          return EMPTY;
        }
        const owners = new Set<string>(action.payload.map((item) => (<any>item).owner));
        return Array.from(owners)
          .filter((owner) => owner)
          .map((id) => Members.RequestByIdAction({ id }));
      })
    )
  );

  fetchCustomersForFeedItems = createEffect(() =>
    this.actions$.pipe(
      ofType(Feed.RESPONSE_SUCCESS_ALL_PAGINATED),
      switchMap((action: any) => {
        if (!action.payload || !action.payload.items) {
          return EMPTY;
        }
        const customers = new Set<string>();

        action.payload.items.forEach((item) => {
          if (item.relatesToModel === 'Memo') {
            let customerIds: string[] = [];
            if (!!item.relatesTo?.customerIds) {
              customerIds = item.relatesTo.customerIds;
            } else if (!!item.meta?.customerIds) {
              customerIds = item.meta.customerIds;
            }
            customerIds.forEach((id: string) => customers.add(id));
          } else {
            customers.add(item.customerId);
          }
        });
        return Array.from(customers).map((id) => Customers.RequestByIdAction({ id }));
      })
    )
  );

  fetchCustomersForChats = createEffect(() =>
    this.actions$.pipe(
      ofType(Chats.RESPONSE_SUCCESS_ALL),
      switchMap((action: any) => {
        if (!action.payload || action.payload.length === 0) {
          return EMPTY;
        }
        const customers = new Set<string>();
        action.payload.forEach((item) => {
          if (item.modelName === 'Customer') {
            customers.add(item.modelId);
          }
        });
        return Array.from(customers).map((id) => Customers.RequestByIdAction({ id }));
      })
    )
  );

  fetchOwnersForMemos = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.RESPONSE_SUCCESS_ALL, Memos.responseSuccessAllPaginatedMemos),
      switchMap((action: any) => {
        const memos = action.items ?? action.payload;
        const owners = new Set<string>(memos.map((memo) => (<any>memo).owner));
        return Array.from(owners)
          .filter((owner) => owner)
          .map((id) => Members.RequestByIdAction({ id }));
      })
    )
  );

  fetchCustomersForMemos = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.RESPONSE_SUCCESS_ALL, Memos.responseSuccessAllPaginatedMemos),
      switchMap((action: any) => {
        const customerIds = new Set<string>();
        const memos = action.items ?? action.payload;
        memos.forEach((memo) => memo.customerIds.forEach((customer) => customerIds.add(customer)));
        return Array.from(customerIds).map((id) => Customers.RequestByIdAction({ id }));
      })
    )
  );

  fetchCustomersForInvitations = createEffect(() =>
    this.actions$.pipe(
      ofType(
        RESPONSE_SUCCESS_INVITATIONS_PENDING,
        RESPONSE_SUCCESS_SEARCH_ALL_PENDING_INVITATIONS_ADMIN
      ),
      switchMap((action: ResponseSuccessPendingInvitations) => {
        const customerIds = new Set<string>();
        action.payload.forEach((invite) =>
          invite.permissions
            ?.filter((permission) => permission.modelName === 'Customer')
            .forEach((permission) => customerIds.add(permission.modelId))
        );
        return Array.from(customerIds).map((id) => Customers.RequestByIdAction({ id }));
      })
    )
  );

  fetchOrganisationsForAdminInvitations = createEffect(() =>
    this.actions$.pipe(
      ofType(RESPONSE_SUCCESS_SEARCH_ALL_PENDING_INVITATIONS_ADMIN),
      switchMap((action: ResponseSuccessSearchPendingInvitationsAdmin) => {
        const organisationIds = new Set<string>();
        action.payload.forEach((invite) => {
          if (!!invite.organisationId) {
            organisationIds.add(invite.organisationId);
          }
        });
        return Array.from(organisationIds).map((id) => Organisations.RequestByIdAction({ id }));
      })
    )
  );

  fetchMembersFromMemoDetailActions = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.RESPONSE_SUCCESS_BY_ID),
      filter((action: any) => action.payload.actions),
      switchMap((action: any) => {
        const actionMembers = new Set<string>(
          action.payload.actions.map((action) => action.memberId)
        );
        return from(
          Array.from(actionMembers)
            .filter((member) => member)
            .map((id) => Members.RequestByIdAction({ id }))
        );
      })
    )
  );

  fetchOwnerForMemo = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.RESPONSE_SUCCESS_BY_ID),
      filter((action: any) => action.payload && action.payload.owner),
      map((action: any) => Members.RequestByIdAction({ id: action.payload.owner }))
    )
  );

  fetchCustomersForMemo = createEffect(
    () => <Observable<Action>>this.actions$.pipe(
        ofType(Memos.RESPONSE_SUCCESS_BY_ID),
        filter((action: any) => action.payload.customerIds),
        switchMap((action: any) =>
          action.payload.customerIds.map((id) => Customers.RequestByIdAction({ id }))
        )
      )
  );

  fetchCustomersForAddedPermissions = createEffect(
    () => <Observable<Action>>this.actions$.pipe(
        ofType(CustomerTeam.RESPONSE_SUCCESS_CREATE),
        filter((action: any) => action.payload.permissions != null),
        switchMap((action: any) =>
          action.payload.permissions.map((permission) =>
            Customers.RequestByIdAction({ id: permission.modelId, forceRefresh: true })
          )
        )
      )
  );

  updateCustomerAfterPermissionAdded = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerTeam.ResponseSuccessAddMemberToCustomer),
      filter((action) => action.permission != null),
      switchMap((action) => [
        Customers.RequestByIdAction({ id: action.permission.modelId, forceRefresh: true }),
      ])
    )
  );

  fetchCustomersForRemovedPermissions = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerTeam.ResponseSuccessRemove),
      filter((action) => action.customerId != null && action.memberId != null),
      map((action) => Customers.RequestByIdAction({ id: action.customerId, forceRefresh: true }))
    )
  );

  fetchCustomersForRequests = createEffect(() =>
    this.actions$.pipe(
      ofType(Requests.RESPONSE_SUCCESS_ALL),
      switchMap((action: any) => {
        const customers = new Set<string>(action.payload.map((request) => (<any>request).modelId));
        return Array.from(customers)
          .filter((customer) => customer)
          .map((id) => Customers.RequestByIdAction({ id }));
      })
    )
  );

  fetchCustomersForCustomerLists = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerLists.RESPONSE_SUCCESS_ALL),
      switchMap((action: any) => {
        const customerIds = new Set<string>(
          (action.payload as CustomerListDto[]).reduce(
            (prev, curr) => [...prev, ...curr.customerIds],
            []
          )
        );
        return Array.from(customerIds).map((id) => Customers.RequestByIdAction({ id }));
      })
    )
  );

  fetchCustomersForCustomerList = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerLists.RESPONSE_SUCCESS_BY_ID),
      switchMap((action: { payload: CustomerListDto; type: any }) => {
        return Array.from(action.payload.customerIds).map((id) =>
          Customers.RequestByIdAction({ id })
        );
      })
    )
  );

  fetchMembersForRequests = createEffect(() =>
    this.actions$.pipe(
      ofType(Requests.RESPONSE_SUCCESS_ALL),
      switchMap((action: any) => {
        const members = new Set<string>(action.payload.map((request) => (<any>request).memberId));
        return Array.from(members)
          .filter((member) => member)
          .map((id) => Members.RequestByIdAction({ id }));
      })
    )
  );

  fetchMembersForReports = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.responseSuccessAllMemoCommentReports),
      switchMap((action) => {
        // Reporters and reported comment creators.
        const involvedUsers = new Set<string>([
          ...action.reports.map((report) => report.comment.memberId),
          ...action.reports.map((report) => report.ownerId),
        ]);
        return Array.from(involvedUsers)
          .filter((id) => id)
          .map((id) => Members.RequestByIdAction({ id }));
      })
    )
  );

  fetchMembersForActivity = createEffect(() =>
    this.actions$.pipe(
      ofType(ResponseSuccessMembersStatisticsAction),
      switchMap((action) => {
        // Reporters and reported comment creators.
        const involvedUsers = new Set<string>(action.payload.map((item) => item.memberId));
        return Array.from(involvedUsers)
          .filter((id) => id)
          .map((id) => Members.RequestByIdAction({ id }));
      })
    )
  );

  fetchMembersForLeaderboards = createEffect(() =>
    this.actions$.pipe(
      ofType(ResponseSuccessFamilyLeaderboardAction),
      switchMap((action) => {
        // Reporters and reported comment creators.
        const involvedUsers = new Set<string>([
          ...action.current.map((report) => report.memberId),
          ...action.previous.map((report) => report.memberId),
        ]);
        return Array.from(involvedUsers)
          .filter((id) => id)
          .map((id) => Members.RequestByIdAction({ id }));
      })
    )
  );

  fetchCustomersForPermissions = createEffect(() =>
    this.actions$.pipe(
      ofType(Members.RESPONSE_SUCCESS_PERMISSIONS_ADMIN),
      switchMap((action: any) => {
        if (!action.payload || !action.payload.permissions) {
          return EMPTY;
        }
        const customers = new Set<string>();

        action.payload.permissions.forEach((permission) => {
          if (permission.modelName === 'Customer' && permission.modelId != null) {
            customers.add(permission.modelId);
          }
        });
        return Array.from(customers).map((id) => Customers.RequestByIdAction({ id }));
      })
    )
  );

  fetchCustomersForNotifications = createEffect(() =>
    this.actions$.pipe(
      ofType(Notifications.RESPONSE_SUCCESS_ALL_PAGINATED),
      switchMap((action: any) => {
        const customers = new Set<string>();
        const notifications: NotificationDto[] = action.payload.items;

        for (let notification of notifications) {
          if (notification.modelName === 'Memo') {
            const customerIds: string[] =
              (notification.model as MemoResponseDto)?.customerIds ?? [];
            for (let id of customerIds) {
              customers.add(id);
            }
          } else if (
            notification.modelName === 'CheckIn' &&
            (notification.model as CheckInDto)?.customerId
          ) {
            customers.add((notification.model as CheckInDto).customerId);
          } else if (notification.modelName === 'PermissionRequest') {
            customers.add((notification.model as PermissionRequestDto).modelId);
          } else if (notification.modelName === 'Invitation') {
            const customerIds = ((notification.model as InvitationDto).permissions ?? [])
              .filter((permission) => permission.modelName === 'Customer')
              .map((permission) => permission.modelId);
            customerIds.forEach((id) => customers.add(id));
          }
        }
        return Array.from(customers).map((id) => Customers.RequestByIdAction({ id }));
      })
    )
  );

  constructor(private actions$: Actions) {}
}
